import React, { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion } from 'framer-motion';
import PageHeader from '../ui/PageHeader';

const policiesData = [
  {
    title: 'Re-enrolments',
    description:
      'To secure your child’s place for the next term, please notify SJ Swimming via email or SMS. If we do not receive confirmation, we may offer the place to another student. We encourage families to confirm promptly to avoid disappointment.',
  },
  {
    title: 'Lesson Payments',
    description:
      'Lesson fees are payable at the beginning of each term. Thank you for ensuring timely payment so we can continue delivering high-quality lessons.',
  },
  {
    title: 'Missed Lessons',
    description:
      'We understand that absences can occur. For illness or sick leave, we require at least 24 hours’ notice to arrange a make-up lesson. For personal leave or travel, two weeks’ notice is required. All missed lessons must be notified in advance, and make-up lessons must be booked accordingly.',
  },
  {
    title: 'Make-Up Lessons',
    description:
      'Students are eligible for up to three make-up lessons per term. Make-up lessons must be used within the same term and cannot be carried over or transferred to holiday programs. If no advance notice is given, a make-up lesson will not be available - this helps us manage spots fairly for all families.',
  },
  {
    title: 'Credits',
    description:
      'Credits are provided in the event of a pool or class cancellation on our end. If this applies to you, please reach out to us directly and we\'ll get it sorted.',
  },
  {
    title: 'Extra Lessons',
    description:
      'Extra lessons are available for students with an existing term booking. We don\'t offer one-off casual bookings during term - our class sizes are planned around regular enrolments, so we need to keep numbers predictable for all families. The fee for extra lessons is $38.00 per lesson for both Learn to Swim and Squad programs.',
  },
];

const Policies = () => {
  const theme = useTheme();
  const [activePolicy, setActivePolicy] = useState(null);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader
        title="Our Policies"
        subtitle="Here’s a quick look at our policies - thank you for your understanding and support! This helps us keep things running smoothly and ensures everyone has a great experience."
      />

      {/* Policies + Images */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.6 }}
      >
      <Grid container spacing={4} alignItems="stretch">
        {/* Left Side: Policies */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          {policiesData.map((policy, index) => (
            <Box
              key={index}
              role="button"
              tabIndex={0}
              aria-expanded={activePolicy === index}
              aria-label={`${policy.title} - read policy`}
              onClick={() => setActivePolicy(activePolicy === index ? null : index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActivePolicy(activePolicy === index ? null : index);
                }
              }}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '20px',
                background: theme.palette.background.paper,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: theme.palette.text.primary,
                boxShadow: '0px 6px 24px rgba(0,0,0,0.3)',
                transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease',
                cursor: 'pointer',
                height: '140px',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                mb: index < policiesData.length - 1 ? 3 : 0,
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: '0px 12px 30px rgba(0,0,0,0.7)',
                  '& .policy-bio': { transform: 'translateY(0)', opacity: 1 },
                },
              }}
            >
              {/* Header (Always Visible) */}
              <Box sx={{ p: 3, textAlign: 'center', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {policy.title}
                </Typography>
              </Box>
              {/* Tap affordance indicator */}
              <Box sx={{ position: 'absolute', bottom: 8, left: 0, right: 0, textAlign: 'center', zIndex: 2, opacity: activePolicy === index ? 0 : 0.35, transition: 'opacity 0.3s ease', pointerEvents: 'none' }}>
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              </Box>

              {/* Description (Slides up on Hover / Tap) */}
              <Box
                className="policy-bio"
                sx={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  bgcolor: '#f7f7f7',
                  color: '#17204a',
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: activePolicy === index ? 'translateY(0)' : 'translateY(100%)',
                  opacity: activePolicy === index ? 1 : 0,
                  transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  zIndex: 3,
                  overflowY: 'auto',
                }}
              >
                <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 500, lineHeight: 1.6 }}>
                  {policy.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Grid>

        {/* Right Side: Images that fill the left column's height */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{
              flex: 1,
              minHeight: { xs: 240, md: 200 },
              overflow: 'hidden',
              borderRadius: '20px',
              boxShadow: '0px 6px 24px rgba(0,0,0,0.3)',
              position: 'relative',
            }}>
              <Box
                component="img"
                src="/images/SJ_Gallery/SQ_Su_1.jpg"
                alt="SJ Swimming squad session"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />
            </Box>
            <Box sx={{
              flex: 1,
              minHeight: { xs: 240, md: 200 },
              overflow: 'hidden',
              borderRadius: '20px',
              boxShadow: '0px 6px 24px rgba(0,0,0,0.3)',
              position: 'relative',
            }}>
              <Box
                component="img"
                src="/images/SJ_Gallery/LTS_2.jpg"
                alt="SJ Swimming learn to swim lesson"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
      </Box>
    </Container>
  );
};

export default Policies;